// src/weather/wxField.js — the rules behind the Field Mapper's weather tool: the
// Weather tab (one row per field) and a field's own weather page.
//
// Kevin, 2026-11-02: "Remember the priorities: data security, data accuracy, awesome
// UX". Both views answer one question per field: how much rain and heat has this
// field had in the window, and how does that run against normal. Before this module
// the tab and the page each did that sum their own way, and they disagreed: a missing
// reading added as 0, a normal that kept running on days with no reading, a season
// that started Apr 1 on one and Apr 20 on the other, "today" taken in UTC, fall wheat
// thrown out, and a 30 mi snap on one against 20 mi on the other.
//
// Pure: no fetch, no React. App.jsx hands in what it read; tools/check-wx-field.js
// drives these same functions offline, so the views and the checker cannot disagree.
//
// ⚠ A MISSING READING IS A GAP, NEVER ZERO. Every total here counts the days it had
// and names the days it expected, and a normal is summed only over days the actual
// has, so actual and normal always cover the same days.
//
// ⚠ ONE INSTRUMENT AGAINST THE NORMAL. The stored normal (wny_grid_climatology) is
// gridMET, so "% of normal" is gridMET over gridMET on the same days. The field's own
// radar rain is shown as rain, and is never divided by that normal.

const WX = require("./wxEvidence");
const WXS = require("./wxSeason");

const num = (v) => {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
const isIso = (s) => typeof s === "string" && /^\d{4}-\d{2}-\d{2}$/.test(s);

// A field farther than this from every grid cell is outside the grid. One number for
// the tab and the page (they were 30 mi and 20 mi). The lattice is ~4 km, so a field
// inside the footprint is always within ~2 mi of a cell; 20 mi only ever catches
// fields beyond the edge of the grid.
const FIELD_CELL_MAX_MI = 20;
// A window counts as complete when at least this share of its days has a reading.
const COMPLETE_SHARE = 0.8;
// The source and years of the stored normal, said the same way everywhere.
const NORMAL_LABEL = "1995–2025 normal";

function haversineMi(a, b) {
  const R = 3958.8, d2r = Math.PI / 180;
  const dLat = (b[0] - a[0]) * d2r, dLon = (b[1] - a[1]) * d2r;
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(a[0] * d2r) * Math.cos(b[0] * d2r) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(s)));
}

// Nearest grid cell to a [lat, lon]. Returns { id, miles, far } or null; far = outside
// the grid, so the caller shows no weather rather than a distant cell's.
function nearestCell(ll, cells) {
  if (!ll || ll[0] == null || ll[1] == null || !Number.isFinite(+ll[0]) || !Number.isFinite(+ll[1])) return null;
  let best = null, bd = Infinity;
  for (const c of cells || []) {
    const lat = num(c.lat), lon = num(c.lon);
    if (lat == null || lon == null) continue;
    const d = haversineMi([+ll[0], +ll[1]], [lat, lon]);
    if (d < bd) { bd = d; best = c; }
  }
  return best ? { id: best.id, miles: bd, far: bd > FIELD_CELL_MAX_MI } : null;
}

// The newest whole day, in the viewer's own calendar. todayIso comes from the caller
// (wxsTodayIso), so a 9 PM viewer in New York isn't handed tomorrow's UTC date.
function yesterday(todayIso) { return WXS.isoAdd(todayIso, -1); }

// The season the crop year names: Apr 1 to Nov 30, ended early at yesterday while it
// is under way. A crop year that hasn't started yet has no season (to = null).
function seasonWindow(cropYear, todayIso) {
  const y = Number(cropYear);
  if (!Number.isFinite(y) || !isIso(todayIso)) return { from: null, to: null, open: false };
  const from = `${y}-04-01`, end = `${y}-11-30`;
  const last = yesterday(todayIso);
  const to = last < end ? last : end;
  return { from, to: to < from ? null : to, open: last < end };
}

// The first day of a "last N days" window ending on `to`.
function lastNFrom(to, n) { return WXS.isoAdd(to, -(Number(n) - 1)); }

// A field's real planting date for the crop year, or null. The acreage report's
// 1900-01-01 sentinel is not a date. A fall-seeded crop (winter wheat) is planted the
// autumn before its crop year, so Aug 1 of the prior year onward is accepted; the old
// tab dropped every planting before Apr 1 and so dropped all of the wheat.
function plantDateFor(reportDate, cropYear, to) {
  if (reportDate == null) return null;
  const d = String(reportDate).slice(0, 10);
  if (!isIso(d) || !isIso(to)) return null;
  const y = Number(cropYear);
  if (!Number.isFinite(y)) return null;
  const earliest = `${y - 1}-08-01`;
  return d >= earliest && d <= to ? d : null;
}

// Daily GDD the way the stored normal was built (wxSeason.gddF), null when either
// temperature is missing. The tab's old helper read a missing day as 0.
function gddOfRow(r) { return r ? WXS.gddF(r.tmax_c, r.tmin_c) : null; }

// One field's totals over a window, from per-day maps.
//   fieldRain: { iso: mm }   the field's own radar rain (field_precip_daily)
//   cellGm:    { iso: mm }   the field's grid cell, gridMET only
//   cellGdd:   { iso: gdd }  the field's grid cell, null days left out
//   climo:     { doy: { p: mm|null, g: gdd|null } }   the cell's normal
//   rainFrom / gddFrom: first day of each window; to: last day.
// Each window ends at the newest day its own source has, when that is within the
// source's normal publishing lag (gridMET runs a day or two behind), so the unpublished
// tail is not counted as missing; that end is returned as *Thru. A source further
// behind than its lag has stalled, and those days DO count as missing.
const LAG_DAYS = { rain: 2, gdd: 3 };
function fieldTotals({ fieldRain = null, cellGm = null, cellGdd = null, climo = null, rainFrom, gddFrom, to }) {
  const out = {
    rainMm: null, rainDays: 0, rainExp: 0, rainOk: false, rainNormMm: null, rainThru: null,
    areaMm: null, areaNormMm: null, areaPct: null, areaDays: 0,
    gdd: null, gddDays: 0, gddExp: 0, gddOk: false, gddNorm: null, gddThru: null, gddPct: null,
  };
  if (!isIso(to)) return out;
  const lastOf = (m) => { let mx = null; if (m) for (const k in m) if (m[k] != null && k <= to && (!mx || k > mx)) mx = k; return mx; };
  const nrm = (iso) => (climo ? climo[WXS.climoDoy(iso)] || null : null);
  const endOf = (thru, lag) => (thru && thru < to && thru >= WXS.isoAdd(to, -lag) ? thru : to);

  if (isIso(rainFrom) && rainFrom <= to) {
    const rThru = lastOf(fieldRain);
    out.rainThru = rThru;
    const end = endOf(rThru, LAG_DAYS.rain);
    let s = 0, n = 0, ns = 0, nn = 0, exp = 0;
    let a = 0, an = 0, ad = 0;
    for (let d = rainFrom; d <= end; d = WXS.isoAdd(d, 1)) {
      exp++;
      const c = nrm(d);
      const p = c ? num(c.p) : null;
      const fr = fieldRain ? num(fieldRain[d]) : null;
      if (fr != null) { s += fr; n++; if (p != null) { ns += p; nn++; } }
      const g = cellGm ? num(cellGm[d]) : null;
      if (g != null && p != null) { a += g; an += p; ad++; }
    }
    out.rainExp = exp;
    out.rainDays = n;
    out.rainMm = n ? s : null;
    out.rainNormMm = nn ? ns : null;
    out.rainOk = n > 0 && n >= Math.ceil(out.rainExp * COMPLETE_SHARE);
    out.areaMm = ad ? a : null;
    out.areaNormMm = ad ? an : null;
    out.areaDays = ad;
    out.areaPct = ad && an > 0 ? (a / an) * 100 : null;
  }

  if (isIso(gddFrom) && gddFrom <= to) {
    const gThru = lastOf(cellGdd);
    out.gddThru = gThru;
    const end = endOf(gThru, LAG_DAYS.gdd);
    let s = 0, n = 0, ns = 0, exp = 0;
    for (let d = gddFrom; d <= end; d = WXS.isoAdd(d, 1)) {
      exp++;
      const v = cellGdd ? num(cellGdd[d]) : null;
      const c = nrm(d);
      const gn = c ? num(c.g) : null;
      // the normal advances only with a reading AND a normal, so both sums cover the same days
      if (v != null && gn != null) { s += v; ns += gn; n++; }
    }
    out.gddExp = exp;
    out.gddDays = n;
    out.gdd = n ? s : null;
    out.gddNorm = n ? ns : null;
    out.gddOk = n > 0 && n >= Math.ceil(out.gddExp * COMPLETE_SHARE);
    out.gddPct = n && ns > 0 ? (s / ns) * 100 : null;
  }
  return out;
}

function countDays(a, b) {
  if (!isIso(a) || !isIso(b) || b < a) return 0;
  return Math.round((Date.parse(b + "T00:00:00Z") - Date.parse(a + "T00:00:00Z")) / 86400000) + 1;
}

// Fold rows of wny_grid_weather_daily into per-cell maps for fieldTotals.
function cellMaps(rows) {
  const gm = {}, gdd = {};
  for (const r of rows || []) {
    if (!r || r.is_forecast === true) continue;
    const d = String(r.obs_date || "").slice(0, 10);
    if (!isIso(d)) continue;
    const g = WX.gridmetPrecipMm(r);
    if (g != null) (gm[r.cell_id] || (gm[r.cell_id] = {}))[d] = g;
    const t = gddOfRow(r);
    if (t != null) (gdd[r.cell_id] || (gdd[r.cell_id] = {}))[d] = t;
  }
  return { gm, gdd };
}

// Fold field_precip_daily rows into { clu(lower): { iso: mm } }. A null reading stays
// out of the map: it is not a dry day.
function fieldRainMap(rows) {
  const acc = {};
  for (const p of rows || []) {
    const k = String(p.clu_id || "").toLowerCase();
    const d = String(p.obs_date || "").slice(0, 10);
    const v = num(p.precip_mm);
    if (!k || !isIso(d) || v == null) continue;
    (acc[k] || (acc[k] = {}))[d] = v;
  }
  return acc;
}

// Fold wny_grid_climatology rows into { cell_id: { doy: { p, g, hi, lo } } }, with the
// longest year_count seen. A missing value stays null, never 0.
function climoMap(rows) {
  const acc = {};
  let years = 0;
  for (const x of rows || []) {
    const c = acc[x.cell_id] || (acc[x.cell_id] = {});
    c[x.day_of_year] = { p: num(x.precip_mm), g: num(x.gdd_f), hi: num(x.tmax_c), lo: num(x.tmin_c) };
    const yc = num(x.year_count);
    if (yc != null && yc > years) years = yc;
  }
  return { byCell: acc, years: years || null };
}

// A field page's daily series over [from, to] for one cell. Every calendar day from
// the first row to the last is present, and a day with no row or no reading is null,
// so a chart drawn from it shows the gap instead of closing it up. Leading days before
// the first row are not padded (the caller says the record starts later).
function fieldSeries({ rows = [], climo = null, from, to }) {
  const by = new Map();
  for (const r of rows || []) {
    if (!r || r.is_forecast === true) continue;
    const d = String(r.obs_date || "").slice(0, 10);
    if (isIso(d) && d >= from && d <= to) by.set(d, r);
  }
  const keys = [...by.keys()].sort();
  const S = { dates: [], precip: [], cumPrecip: [], gdd: [], cumGdd: [], hi: [], lo: [], bands: [], counted: [],
    nPD: [], nPC: [], nGD: [], nGC: [], nHi: [], nLo: [],
    totPrecip: 0, totGdd: 0, normP: null, normG: null, heat: 0, freeze: 0, wettest: null,
    missing: 0, gaps: 0, gddMissing: 0, lastGm: null, radarTail: null, radarTailDays: 0,
    covStart: keys[0] || null, covEnd: keys[keys.length - 1] || null, nDays: 0,
    maxHi: null, minLo: null, avgHi: null, avgLo: null };
  if (!keys.length) return S;
  let cp = 0, cg = 0, np = 0, ng = 0, anyN = false;
  let hiS = 0, hiN = 0, loS = 0, loN = 0, tail = 0, tailN = 0;
  for (let d = keys[0]; d <= keys[keys.length - 1]; d = WXS.isoAdd(d, 1)) {
    const r = by.get(d) || null;
    if (!r) S.gaps++;
    const c = climo ? climo[WXS.climoDoy(d)] || null : null;
    const tH = r ? WX.cToF(num(r.tmax_c)) : null;
    const tL = r ? WX.cToF(num(r.tmin_c)) : null;
    const gmMm = r ? WX.gridmetPrecipMm(r) : null;
    const p = gmMm == null ? null : gmMm / 25.4;
    if (p == null) {
      S.missing++;
      const rr = r ? WX.radarPrecipMm(r) : null;
      if (rr != null) { tail += rr / 25.4; tailN++; }
    } else { S.lastGm = d; tail = 0; tailN = 0; }
    const g = gddOfRow(r);
    if (g == null) S.gddMissing++;
    const pn = c && c.p != null ? c.p / 25.4 : null;
    const gn = c ? c.g : null;
    if (p != null) cp += p;
    if (g != null) cg += g;
    // the normal advances only on days the actual has a reading
    if (p != null && pn != null) { np += pn; anyN = true; }
    if (g != null && gn != null) { ng += gn; anyN = true; }
    S.dates.push(d); S.counted.push(p != null);
    S.precip.push(p); S.cumPrecip.push(cp);
    S.gdd.push(g); S.cumGdd.push(cg);
    S.hi.push(tH); S.lo.push(tL); S.bands.push([tL, tH]);
    S.nPD.push(p != null ? pn : null); S.nPC.push(np);
    S.nGD.push(gn); S.nGC.push(ng);
    S.nHi.push(c && c.hi != null ? WX.cToF(c.hi) : null);
    S.nLo.push(c && c.lo != null ? WX.cToF(c.lo) : null);
    if (tH != null) { hiS += tH; hiN++; if (tH > 90) S.heat++; if (S.maxHi == null || tH > S.maxHi) S.maxHi = tH; }
    if (tL != null) { loS += tL; loN++; if (tL < 32) S.freeze++; if (S.minLo == null || tL < S.minLo) S.minLo = tL; }
    if (p != null && (S.wettest == null || p > S.wettest)) S.wettest = p;
  }
  S.totPrecip = cp; S.totGdd = cg;
  S.normP = anyN ? np : null; S.normG = anyN ? ng : null;
  S.avgHi = hiN ? hiS / hiN : null; S.avgLo = loN ? loS / loN : null;
  S.radarTail = tailN ? tail : null; S.radarTailDays = tailN;
  S.nDays = S.dates.length;
  return S;
}

// GDD from planting through the series' end, or why there is none.
function gddSincePlant(series, plantISO) {
  if (!plantISO) return null;
  let g = 0, n = 0;
  for (let i = 0; i < series.dates.length; i++) {
    if (series.dates[i] >= plantISO && series.gdd[i] != null) { g += series.gdd[i]; n++; }
  }
  return n ? g : null;
}

// "Apr 1" / "Apr 1, 2026": the x axis of a chart, with the year where it changes.
const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
function shortDate(iso, withYear = false) {
  if (!isIso(iso)) return "";
  const s = `${MON[+iso.slice(5, 7) - 1]} ${+iso.slice(8, 10)}`;
  return withYear ? `${s}, ${iso.slice(0, 4)}` : s;
}

module.exports = {
  FIELD_CELL_MAX_MI, COMPLETE_SHARE, NORMAL_LABEL, LAG_DAYS,
  haversineMi, nearestCell, yesterday, seasonWindow, lastNFrom, plantDateFor, gddOfRow,
  fieldTotals, countDays, cellMaps, fieldRainMap, climoMap, fieldSeries, gddSincePlant, shortDate,
};
